import React from 'react';
import styled from 'styled-components';
import { UserRoleKey } from '../types';
import { getRoleDisplayName } from '../helpers/fieldHelpers';

type Props = {
  roles: UserRoleKey[];
  selectedRole: UserRoleKey | null;
  onSelect: (roleKey: UserRoleKey) => void;
  isVisible: boolean;
};

const RoleList: React.FC<Props> = ({ roles, selectedRole, onSelect, isVisible }) => {
  if (!isVisible) return null;

  return (
    <ColumnWrapper>
      <TitleWrapper>תפקידים</TitleWrapper>
      <ListWrapper>
        {roles.map((roleKey) => {
          const isSelected = selectedRole === roleKey;
          return (
            <Item
              key={roleKey}
              $isSelected={isSelected}
              onClick={() => onSelect(roleKey)}
              tabIndex={0}
              role="button"
              aria-label={getRoleDisplayName(roleKey)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelect(roleKey);
              }}
            >
              <RoleName>{getRoleDisplayName(roleKey)}</RoleName>
              {/* <RoleKey>{roleKey}</RoleKey> */}
            </Item>
          );
        })}
      </ListWrapper>
    </ColumnWrapper>
  );
};

export default RoleList;

const ColumnWrapper = styled.div`
  min-width: 25%;
  height: 100%;
  display: flex;
  flex-flow: column;
  gap: 1em;
  padding: 0.75em;
  background: var(--bg-light, #f5f7fa);
  border-radius: 25px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0.75em 1em;
  font-weight: 600;
  font-size: 1.1em;
  color: var(--primary-green, #297f76);
  font-family: 'Assistant', sans-serif;
  line-height: 1;
`;

const ListWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  gap: 0.5em;
  padding: 0.75em 1em;
  background: var(--bg-white, #ffffff);
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 1px 3px var(--shadow-light, rgba(0, 0, 0, 0.06));
  &::-webkit-scrollbar {
    width: 0;
  }
  scrollbar-width: none;
`;

const Item = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75em;
  padding: 0.75em 1em;
  background: ${(props) =>
    props.$isSelected
      ? 'linear-gradient(135deg, var(--primary-green, #297F76) 0%, #1e8449 100%)'
      : '#f8f9fa'};
  color: ${(props) => (props.$isSelected ? '#ffffff' : '#6b7280')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${(props) =>
    props.$isSelected
      ? '0 2px 6px rgba(41, 127, 118, 0.2)'
      : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  outline: none;

  &:hover {
    background: ${(props) =>
      props.$isSelected ? 'rgba(41, 127, 118, 0.9)' : '#e8f5e9'};
    color: ${(props) => (props.$isSelected ? '#ffffff' : '#4b5563')};
    box-shadow: ${(props) =>
      props.$isSelected
        ? '0 3px 8px rgba(41, 127, 118, 0.3)'
        : '0 2px 4px rgba(41, 127, 118, 0.15)'};
  }
`;

const RoleName = styled.span`
  font-weight: 600;
  font-size: 1em;
`;

const RoleKey = styled.span`
  font-size: 0.75em;
  opacity: 0.6;
  direction: ltr;
`;
